/* VELVOON interactions: hero slider, scroll reveal, story parallax, fit toggle, menu drawer, payment badges. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.documentElement.classList.add('vv-js');

  /* Scroll reveal ----------------------------------------------------- */
  const revealAll = (root = document) => {
    const els = root.querySelectorAll('[data-vv-reveal]:not(.is-in)');
    if (!('IntersectionObserver' in window) || reduce) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach((el) => io.observe(el));
  };

  /* Hero slider ------------------------------------------------------- */
  const initHero = (hero) => {
    if (hero.dataset.ready) return;
    hero.dataset.ready = '1';
    const slides = [...hero.querySelectorAll('.vv-hero__slide')];
    const dots = [...hero.querySelectorAll('.vv-hero__dot')];
    const bar = hero.querySelector('.vv-hero__progress span');
    const delay = (parseInt(hero.dataset.speed, 10) || 6) * 1000;
    let i = 0, timer = null;
    hero.style.setProperty('--dur', delay / 1000 + 's');

    const go = (n) => {
      if (!slides.length) return;
      i = (n + slides.length) % slides.length;
      slides.forEach((s, k) => { s.classList.toggle('is-active', k === i); s.setAttribute('aria-hidden', k !== i); });
      dots.forEach((d, k) => { d.classList.toggle('is-active', k === i); d.setAttribute('aria-current', k === i); });
      restart();
    };
    const restart = () => {
      clearTimeout(timer);
      if (bar) { hero.classList.remove('is-playing'); void bar.offsetWidth; }
      if (slides.length < 2 || reduce || hero.dataset.autoplay === 'false' || hero.matches(':hover')) return;
      hero.classList.add('is-playing');
      timer = setTimeout(() => go(i + 1), delay);
    };

    dots.forEach((d, k) => d.addEventListener('click', () => go(k)));
    hero.addEventListener('mouseenter', () => { clearTimeout(timer); hero.classList.remove('is-playing'); });
    hero.addEventListener('mouseleave', restart);
    hero.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') go(i + 1);
      if (e.key === 'ArrowLeft') go(i - 1);
    });
    let x0 = null;
    hero.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    hero.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 45) go(dx < 0 ? i + 1 : i - 1);
      x0 = null;
    });
    document.addEventListener('visibilitychange', () => (document.hidden ? clearTimeout(timer) : restart()));
    go(0);
  };

  /* Story parallax ---------------------------------------------------- */
  const parallax = () => {
    if (reduce) return;
    const imgs = [...document.querySelectorAll('[data-vv-parallax] img')];
    if (!imgs.length) return;
    let ticking = false;
    const run = () => {
      imgs.forEach((img) => {
        const r = img.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > innerHeight) return;
        const p = (r.top + r.height / 2 - innerHeight / 2) / innerHeight;
        img.style.transform = `translate3d(0, ${(-7 + p * -10).toFixed(2)}%, 0)`;
      });
      ticking = false;
    };
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(run); } }, { passive: true });
    run();
  };

  /* Fit toggle -------------------------------------------------------- */
  const initFit = (root) => {
    root.querySelectorAll('[data-fit-btn]').forEach((b) =>
      b.addEventListener('click', () => {
        root.dataset.fit = b.dataset.fitBtn;
        root.querySelectorAll('[data-fit-btn]').forEach((x) => x.setAttribute('aria-pressed', x === b));
      })
    );
  };

  /* Menu drawer: add Velvoon brand bar + footer (markup from <template id="vv-drawer-tpl">) */
  const dressDrawer = () => {
    const tpl = document.getElementById('vv-drawer-tpl');
    if (!tpl) return;
    document.querySelectorAll('header-drawer .menu-drawer').forEach((d) => {
      if (d.querySelector('.vv-dr__head')) return;
      const frag = tpl.content.cloneNode(true);
      const head = frag.querySelector('.vv-dr__head');
      const foot = frag.querySelector('.vv-dr__foot');
      const nav = d.querySelector(':scope > .menu-drawer__navigation');
      if (head) (nav ? d.insertBefore(head, nav) : d.prepend(head));
      if (foot) d.appendChild(foot);
      d.classList.add('vv-dressed');
    });
  };
  document.addEventListener('toggle', (e) => { if (e.target.matches?.('.menu-drawer-container')) dressDrawer(); }, true);

  /* Cart: show accepted payment methods under the checkout button (cart drawer re-renders, so watch for it) */
  const payCart = () => {
    const tpl = document.getElementById('vv-pay-tpl');
    if (!tpl) return;
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((btn) => {
      const host = btn.parentElement;
      if (!host || host.parentElement?.querySelector(':scope > .vv-pay--cart')) return;
      const el = tpl.content.firstElementChild?.cloneNode(true);
      if (!el) return;
      el.classList.add('vv-pay--cart');
      host.insertAdjacentElement('afterend', el);
    });
  };
  let payTimer;
  new MutationObserver(() => { clearTimeout(payTimer); payTimer = setTimeout(payCart, 150); })
    .observe(document.documentElement, { childList: true, subtree: true });

  const boot = (root = document) => {
    dressDrawer();
    payCart();
    root.querySelectorAll('.vv-hero').forEach(initHero);
    root.querySelectorAll('.vv-fit').forEach(initFit);
    revealAll(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { boot(); parallax(); });
  else { boot(); parallax(); }

  /* Theme editor: re-init sections when they are re-rendered */
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.vv-hero__slide');
    if (!slide) return;
    const hero = slide.closest('.vv-hero');
    const idx = [...hero.querySelectorAll('.vv-hero__slide')].indexOf(slide);
    hero.querySelectorAll('.vv-hero__dot')[idx]?.click();
  });
})();
